import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Close as CloseIcon } from '@mui/icons-material';
import { modalStyles } from '../styles/modalStyles';
import PlansModal from './PlansModal';
import { fetchActivePlans, ActivePlan } from '../services/companyService';
import { fetchCompanySubscriptionDetails, UsageStatisticsFromApi, nonNegativeUsageStat } from '../services/subscriptionService';
import { STORAGE_KEYS, DISABLE_SUBSCRIPTION, SUBSCRIPTION_LIMIT_MESSAGES } from '../helpers/constants';
import { LanguageConstants } from '@/helpers/LanguageConstants';
import { getCompanyId, getZohoSubscriptionId } from '@/helpers/storageUtils';
import { formatSubscriptionStorageUsedOfLimit } from '@/helpers/subscriptionUsageFormat';

interface ChannelRestrictionModalProps {
  open: boolean;
  onClose: () => void;
  onViewPlans?: () => void; // Optional callback when View Plans is clicked
  restrictionType?: 'channel' | 'message' | 'attachment'; // Type of restriction: 'channel' for channel creation, 'message' for message posting, 'attachment' for attachment upload
}

// Interface for usage data (storage row uses byte counts from API)
interface UsageData {
  label: string;
  current: number;
  limit: number;
  isStorageBytes?: boolean; // When true, current/limit are bytes → "X MB of Y MB" (or GB when large)
}

// Single usage line: small rounded-square bullet (not a disc, bar, or radio control)
const UsageRow: React.FC<UsageData> = ({ label, current, limit, isStorageBytes }) => {
  const valueText = isStorageBytes
    ? formatSubscriptionStorageUsedOfLimit(current, limit) // Bytes → e.g. "47.2 MB of 50.0 MB"
    : `${current} of ${limit}`; // Channels/Messages: plain counts
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Box
        component="span"
        aria-hidden
        sx={(theme) => ({
          width: 7,
          height: 7,
          flexShrink: 0,
          borderRadius: '2px',
          bgcolor: alpha(theme.palette.primary.main, 0.5),
        })}
      />
      <Typography variant="body2" sx={{ flex: 1 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
        {valueText}
      </Typography>
    </Box>
  );
};

/**
 * Reusable modal component to display channel creation or message posting restrictions
 * Shows current plan information and provides option to view available plans
 */
const ChannelRestrictionModal: React.FC<ChannelRestrictionModalProps> = ({
  open,
  onClose,
  onViewPlans,
  restrictionType = 'channel', // Default to 'channel' for backward compatibility
}) => {
  const [plansModalOpen, setPlansModalOpen] = useState<boolean>(false);
  const [activePlan, setActivePlan] = useState<ActivePlan | null>(null);
  const [planNameFromSubscription, setPlanNameFromSubscription] = useState<string>('');
  // Zoho/catalog plan code from company subscription API — forwarded to PlansModal for "Current Plan" badge
  const [planCodeFromSubscription, setPlanCodeFromSubscription] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [usageData, setUsageData] = useState<UsageData[]>([]);

  // Map usageStatistics from API to UsageData rows: Channels, Messages, Data Storage
  const mapUsageStatisticsToUsageData = (raw: UsageStatisticsFromApi): UsageData[] => {
    const r = raw as unknown as Record<string, unknown>;
    const channelLimit = nonNegativeUsageStat(raw.channelLimit);
    const channelAvailable = nonNegativeUsageStat(raw.channelavailable ?? r.channelAvailable);
    const messageLimit = nonNegativeUsageStat(raw.messageLimit);
    const messageAvailable = nonNegativeUsageStat(raw.messageavailable ?? r.messageAvailable);
    const storageLimit = nonNegativeUsageStat(raw.StorageLimit ?? r.storageLimit);
    const storageAvailable = nonNegativeUsageStat(raw.Storageavailable ?? r.storageAvailable);
    const channelUsed = Math.max(0, channelLimit - channelAvailable);
    const messageUsed = Math.max(0, messageLimit - messageAvailable);
    const storageUsed = Math.max(0, storageLimit - storageAvailable);
    return [
      { label: 'Channels', current: channelUsed, limit: channelLimit },
      { label: 'Messages', current: messageUsed, limit: messageLimit },
      { label: 'Data Storage', current: storageUsed, limit: storageLimit, isStorageBytes: true },
    ];
  };

  // When subscription is disabled we only show SUBSCRIPTION_LIMIT_MESSAGES; skip fetch to avoid "No subscription found for company"
  const emptyUsageData: UsageData[] = [
    { label: 'Channels', current: 0, limit: 0 },
    { label: 'Messages', current: 0, limit: 0 },
    { label: 'Data Storage', current: 0, limit: 0, isStorageBytes: true },
  ];

  // Fetch subscription details (usageStatistics) and optional active plan when modal opens
  useEffect(() => {
    if (open) {
      const loadData = async () => {
        try {
          setLoading(true);
          setError(null);
          // When VITE_DISABLE_SUBSCRIPTION is true, skip API call — show only SUBSCRIPTION_LIMIT_MESSAGES, never "No subscription found for company"
          if (DISABLE_SUBSCRIPTION) {
            setUsageData(emptyUsageData);
            setLoading(false);
            return;
          }
          const companyId = getCompanyId();
          if (companyId) {
            // Fetch usage from fetchCompanySubscriptionDetails (company_id as payload)
            const response = await fetchCompanySubscriptionDetails(companyId);
            if (response.success && response.data) {
              setPlanNameFromSubscription(response.data.currentPlan || '');
              setPlanCodeFromSubscription((response.data.currentPlanCode || '').trim());
              if (response.data.usageStatistics) {
                setUsageData(mapUsageStatisticsToUsageData(response.data.usageStatistics));
              } else {
                setUsageData(emptyUsageData);
              }
            } else {
              throw new Error(response.message || 'Failed to fetch subscription details');
            }
          } else {
            // Fallback: fetch active plan only when no companyId (e.g. not logged in)
            const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) ||
              localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN) ||
              undefined;
            const planResponse = await fetchActivePlans(token);
            if (planResponse.success && planResponse.data) {
              setActivePlan(planResponse.data);
            }
            setPlanCodeFromSubscription(''); // No company subscription payload — avoid marking a wrong plan as current
            setUsageData(emptyUsageData);
          }
        } catch (err) {
          const errMsg = err instanceof Error ? err.message : 'Failed to load usage';
          // When subscription disabled, never show "No subscription found for company" — show limit message only
          if (DISABLE_SUBSCRIPTION && /no subscription found for (the )?company/i.test(errMsg)) {
            setError(null);
            setUsageData(emptyUsageData);
          } else {
            console.error('Error loading subscription/usage:', err);
            setError(errMsg);
          }
        } finally {
          setLoading(false);
        }
      };

      loadData();
    }
  }, [open]);

  // Handle View Plans button click
  const handleViewPlans = () => {
    // Call optional callback if provided
    if (onViewPlans) {
      onViewPlans();
    }
    // Open plans modal
    setPlansModalOpen(true);
  };

  // Handle plans modal close
  const handlePlansModalClose = () => {
    setPlansModalOpen(false);
  };

  // Get plan name for display (from subscription details first, then active plan)
  const planName = planNameFromSubscription || activePlan?.plan_name || 'Current Plan';

  // When subscription is disabled, show admin-contact messages; otherwise show upgrade-plan messaging
  const showUpgradePlan = !DISABLE_SUBSCRIPTION;

  // Get restriction message based on restriction type and subscription config
  const getRestrictionMessage = () => {
    if (DISABLE_SUBSCRIPTION) {
      // Subscription disabled: direct user to contact admin
      if (restrictionType === 'message') return SUBSCRIPTION_LIMIT_MESSAGES.MESSAGE;
      if (restrictionType === 'attachment') return SUBSCRIPTION_LIMIT_MESSAGES.DATA_STORAGE;
      return SUBSCRIPTION_LIMIT_MESSAGES.CHANNEL;
    }
    // Subscription enabled: show upgrade-plan copy
    if (restrictionType === 'message') {
      return (
        <>
          Your current <b>{planName}</b> plan has restrictions on <b>AI Messages Generation</b>. 
          Upgrade your plan to generate more AI Messages and access additional features.
        </>
      );
    }
    if (restrictionType === 'attachment') {
      return (
        <>
          Your current <b>{planName}</b> plan has restrictions on <b>Data Storage</b>. 
          Upgrade your plan to add more files and access additional features.
        </>
      );
    }
    // Default to channel restriction message
    return (
      <>
        Your current <b>{planName}</b> plan has restrictions on <b>{LanguageConstants.topic} creation</b>. 
        Upgrade your plan to create more {LanguageConstants.topic_s} and access additional features.
      </>
    );
  };

  // Modal title: "Limit Reached" when subscription disabled, else "Upgrade your plan"
  const modalTitle = showUpgradePlan ? 'Upgrade your plan' : 'Limit Reached';

  return (
    <>
      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            onClose();
          }
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            ...modalStyles.dialog['& .MuiDialog-paper'],
            minHeight: 280,
          },
        }}
      >
        <DialogTitle sx={modalStyles.dialogTitle}>
          <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
            {modalTitle}
          </Typography>
          <IconButton
            onClick={onClose}
            sx={modalStyles.closeButton}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            pt: 2,
            pb: 1,
            ...(showUpgradePlan ? {} : { display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: 160 }),
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : (
            <>
              {/* When subscription disabled: message block centered horizontally and vertically, text left-aligned inside it */}
              {showUpgradePlan ? (
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 3, lineHeight: 1.7 }}
                >
                  {getRestrictionMessage()}
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                  <Typography
                    variant="body1"
                    component="div"
                    color="text.secondary"
                    sx={{
                      lineHeight: 1.7,
                      textAlign: 'left',
                      border: '1px solid',
                      borderColor: 'primary.main',
                      backgroundColor: (theme) => `${theme.palette.primary.main}14`,
                      borderRadius: 1,
                      py: 2,
                      px: 2,
                      width: 'fit-content',
                      maxWidth: '100%',
                    }}
                  >
                    {getRestrictionMessage()}
                  </Typography>
                </Box>
              )}

              {/* Usage section - only when subscription enabled (upgrade flow) */}
              {showUpgradePlan && (
                <Box sx={{ mb: 2, mt: 3 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      mb: 1.5,
                      color: 'text.primary',
                    }}
                  >
                    Usage
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {usageData.map((usage, index) => (
                      <UsageRow
                        key={index}
                        label={usage.label}
                        current={usage.current}
                        limit={usage.limit}
                        isStorageBytes={usage.isStorageBytes}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </>
          )}
        </DialogContent>

        {/* Show Cancel and (when subscription enabled) View Plans when data has loaded */}
        {!loading && (
          <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
            <Button
              onClick={onClose}
              variant={showUpgradePlan ? 'outlined' : 'contained'}
              sx={{
                borderColor: 'primary.main',
                color: 'primary.main',
                ...(showUpgradePlan ? {} : { backgroundColor: 'primary.main', color: 'white' }),
                '&:hover': {
                  borderColor: 'primary.dark',
                  backgroundColor: showUpgradePlan ? 'rgba(25, 118, 210, 0.04)' : 'primary.dark',
                },
              }}
            >
              {showUpgradePlan ? 'Cancel' : 'Close'}
            </Button>
            {showUpgradePlan && (
              <Button
                onClick={handleViewPlans}
                variant="contained"
                sx={{
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                }}
              >
                View Plans
              </Button>
            )}
          </DialogActions>
        )}
      </Dialog>

      {/* Plans Modal */}
      <PlansModal
        open={plansModalOpen}
        onClose={handlePlansModalClose}
        title="Upgrade Plan"
        subtitle="Choose a plan that better fits your needs"
        autoFetch={true}
        showCurrentPlanBadge={true}
        gridColumns={{ xs: 1, md: 2, lg: 3 }}
        currentPlanCode={planCodeFromSubscription || undefined}
        currentPlanName={planNameFromSubscription.trim() ? planNameFromSubscription : undefined}
        subscriptionId={getZohoSubscriptionId() || undefined}
      />
    </>
  );
};

export default ChannelRestrictionModal;
